// Interaktion: Was der Avatar im Blick hat und was er damit tun kann. Generisch nach
// Architektur-Regel 5: Ziel im Blick plus Taste ergibt eine Liste möglicher Handlungen. Melken ist
// die erste; Scheren, Eier sammeln und weitere kommen als Produkt-Einträge der Tierart dazu, nicht
// als Code hier. Reine Logik ohne DOM, deterministisch; die Einzelheiten stehen in ADR 0009.
//
// Seit task-013 (ADR 0027) gehören auch die festen Dinge einer Parzelle dazu – Tor, Apfelbaum und
// Kiste – und der Besitz entscheidet mit: Handlungen gibt es nur auf der eigenen Parzelle. Die
// Ausführung liegt beim Server; der Client sendet die Absicht und wartet auf `actionResult`.
// `performAction` bleibt für den Server und die Tests.

import { SPECIES, getSpecies, isProductReady, type Aabb, type AnimalState, type ProductDef, type Species } from '../animals/index.js';
import { addItem } from '../inventory/index.js';
import type { ItemId } from '../inventory/items.js';
import { FARM_DAY_SECONDS } from '../protocol/clock.js';
import type { FarmState, InteractTarget, ParcelTargetKind } from '../protocol/messages.js';
import { PLAYER, TICK_RATE, type GameState, type PlayerState, type Vec3 } from '../sim/state.js';
import { ZAUN_HOEHE } from '../world/blocks.js';
import { HOF_PLAN, parcelLocalRect, type Parcel, type Punkt } from '../world/parcels.js';
import { PREFABS } from '../world/prefabs.js';

/**
 * Was der Avatar anschaut; `distance` ist die Strecke vom Auge bis zum Quader in Blöcken. Ein Ziel
 * ist entweder ein Tier oder eines der festen Dinge einer Parzelle (Tor, Baum, Kiste). Die beiden
 * Formen tragen dieselben Felder wie `InteractTarget` im Protokoll, damit der Client sein Ziel
 * unverändert an den Server schicken kann.
 */
export type Target =
  | { kind: 'animal'; animalId: string; distance: number }
  | { kind: ParcelTargetKind; parcelId: number; distance: number };

/**
 * Eine mögliche Handlung am Ziel. `id` ist `<Produkt>-<Tier-id>` bzw. `<Art>-<Parzelle>`, eindeutig
 * in der Liste; `label` und `key` kommen aus dem Produkt der Art bzw. aus `PARCEL_ACTIONS`.
 * `target` ist das Ziel in der Form, die auch die `interact`-Nachricht trägt.
 */
export interface Action {
  id: string;
  label: string;
  key: string;
  target: InteractTarget;
}

/**
 * Die Handlungen an den festen Dingen einer Parzelle – Daten wie die Produkte einer Tierart
 * (Architektur-Regel 4 und 5). Ein weiteres Ding ist ein Eintrag hier plus ein Zielquader, kein
 * Code im Kern.
 */
export const PARCEL_ACTIONS = {
  /** Das Tor: eine Taste, zwei Beschriftungen je nach Zustand. */
  gate: { key: 'KeyE', offen: 'Schließen', zu: 'Öffnen' },
  /**
   * Der Apfelbaum gibt beim Pflücken alle Äpfel auf einmal, solange welche hängen. Danach trägt er
   * `nachwachsenHofSekunden` später wieder `ertrag` Äpfel – gezählt auf der Hof-Uhr, die nur läuft,
   * solange der Besitzer da ist. Das Nachwachsen rechnet der Server (task-014, ADR 0031).
   */
  tree: {
    key: 'KeyE',
    action: 'Äpfel pflücken',
    item: 'apfel' as ItemId,
    ertrag: 3,
    nachwachsenHofSekunden: 2 * FARM_DAY_SECONDS,
  },
  /** Die Kiste geht auf; was darin liegt, zeigt der Client (task-014). */
  chest: { key: 'KeyE', action: 'Öffnen' },
} as const;

/** Die festen Dinge einer Parzelle in der Reihenfolge, in der sie beim Zielen geprüft werden. */
export const PARCEL_TARGET_KINDS: readonly ParcelTargetKind[] = ['gate', 'tree', 'chest'];

/**
 * Höhe des Zielquaders über dem Fuß des Blocks. Das Tor ist so hoch wie der Zaun, an dem es hängt,
 * die Kiste einen Block, der Apfelbaum reicht mit seinem Stamm bis in die Krone.
 */
const ZIEL_HOEHE: Readonly<Record<ParcelTargetKind, number>> = {
  gate: ZAUN_HOEHE,
  tree: 5,
  chest: 1,
};

/** Die erste Ebene des Apfelbaums mit Laub: Dort beginnt die Krone. */
const KRONE_AB = PREFABS.apfelbaum.layers.findIndex((ebene) => ebene.some((zeile) => zeile.includes('L')));

type SpeciesTable = Readonly<Record<string, Species>>;

/**
 * Umgebung der Interaktion. Ohne `parcels` gibt es nur Tier-Ziele und keinen Besitz – so sieht die
 * Welt der Technik-Demo aus, die keine Höfe kennt. Mit `parcels` gilt: Handlungen nur auf der
 * eigenen Parzelle (`ownParcelId`). `farms` liefert den Zustand, den die Blöcke nicht hergeben –
 * ob das Tor offen ist und wie viele Äpfel hängen.
 */
export interface InteractionContext {
  /** Arten-Tabelle; Vorgabe `SPECIES` (ADR 0003). */
  species?: SpeciesTable;
  /** Die Parzellen der Welt; ordnet feste Dinge und Tiere ihrem Hof zu. */
  parcels?: readonly Parcel[];
  /** Zustand der Höfe; `performAction` verändert ihn (Tor, Äpfel). */
  farms?: readonly FarmState[];
  /** Die eigene Parzelle, oder null ohne Hof. */
  ownParcelId?: number | null;
}

/**
 * Der Strahl von Augenhöhe in Blickrichtung gegen die Quader aller Tiere und – sofern `parcels`
 * mitkommt – gegen Tor, Baum und Kiste jeder Parzelle; das nächste Ziel innerhalb von
 * `PLAYER.reach` gewinnt, sonst `null`. Der Tierquader hat Breite, Länge und Höhe der Art, steht
 * mit der Grundfläche um den Fußpunkt und dreht sich mit `yaw` des Tiers. Blöcke zwischen Auge und
 * Ziel werden nicht geprüft (ADR 0009). Verändert `state` nicht.
 */
export function findTarget(state: GameState, kontext: InteractionContext = {}): Target | null {
  const auge = augenpunkt(state.player);
  const richtung = blickrichtung(state.player.yaw, state.player.pitch);
  const table = kontext.species ?? SPECIES;

  let ziel: Target | null = null;
  const naeher = (distance: number | null): distance is number =>
    distance !== null && distance <= PLAYER.reach && (ziel === null || distance < ziel.distance);

  for (const animal of state.animals) {
    const distance = strahlGegenTier(auge, richtung, animal, getSpecies(animal.species, table));
    if (naeher(distance)) ziel = { kind: 'animal', animalId: animal.id, distance };
  }

  for (const parcel of kontext.parcels ?? []) {
    for (const kind of PARCEL_TARGET_KINDS) {
      for (const quader of parcelTargetAabbs(parcel, kind)) {
        // Billige Vorprüfung: Was schon als Punkt zu weit weg ist, kann der Strahl nicht näher treffen.
        if (aabbDistance(auge, quader) > PLAYER.reach) continue;
        const distance = strahlGegenQuader(auge, richtung, quader);
        if (naeher(distance)) ziel = { kind, parcelId: parcel.id, distance };
      }
    }
  }
  return ziel;
}

/**
 * Macht aus einem Ziel des Protokolls ein Ziel im Blick, samt Abstand von der Augenhöhe. Der
 * Server prüft damit eine `interact`-Nachricht, ohne dem Client zu glauben: Er rechnet den Abstand
 * selbst und lehnt alles über `PLAYER.reach` ab. `null`, wenn es das Tier oder die Parzelle nicht
 * gibt.
 */
export function resolveTarget(state: GameState, target: InteractTarget, kontext: InteractionContext = {}): Target | null {
  const auge = augenpunkt(state.player);
  if (target.kind === 'animal') {
    const animal = tierZu(state, target.id);
    if (animal === undefined) return null;
    const species = getSpecies(animal.species, kontext.species ?? SPECIES);
    return { kind: 'animal', animalId: animal.id, distance: abstandZuTier(auge, animal, species) };
  }
  const parcel = (kontext.parcels ?? []).find((p) => p.id === target.parcelId);
  if (parcel === undefined) return null;
  const distance = Math.min(...parcelTargetAabbs(parcel, target.kind).map((quader) => aabbDistance(auge, quader)));
  return { kind: target.kind, parcelId: parcel.id, distance };
}

/**
 * Die Handlungen am Ziel: am Tier je Produkt der Art eine, sofern das Produkt bereit ist; am Tor
 * „Öffnen“ oder „Schließen“, am Baum „Äpfel pflücken“, solange Äpfel hängen, an der Kiste
 * „Öffnen“. Ohne Ziel, an einem fremden Hof oder bei unbekanntem Zustand leer. Die Bereitschaft
 * gilt je Tier (`productReadyTick`), also für alle seine Produkte zugleich.
 */
export function availableActions(state: GameState, target: Target | null, kontext: InteractionContext = {}): Action[] {
  if (target === null) return [];
  if (target.kind === 'animal') {
    const animal = tierZu(state, target.animalId);
    if (animal === undefined || !isProductReady(animal, state.tick)) return [];
    if (!darfHandeln(parzelleZuTier(animal, kontext), kontext)) return [];
    return getSpecies(animal.species, kontext.species ?? SPECIES).products.map((product) => tierHandlung(product, animal));
  }
  if (!darfHandeln(target.parcelId, kontext)) return [];
  const handlung = parzellenHandlung(target.kind, target.parcelId, hofZu(kontext, target.parcelId));
  return handlung === null ? [] : [handlung];
}

/**
 * Führt die Handlung aus und meldet, ob sie geglückt ist. Am Tier: Produkt ins Inventar,
 * `productReadyTick = tick + cooldownSeconds · TICK_RATE`. Am Tor: auf oder zu. Am Baum: alle Äpfel
 * ins Inventar, der Baum ist danach leer; wann er wieder trägt, rechnet der Server (task-014). An der
 * Kiste: nichts – sie zu öffnen ist Sache der Ansicht und der Server merkt es sich. `false` bei unbekanntem Ziel, unbekanntem Produkt, leerem Tier, leerem Baum oder
 * unbekanntem Hofzustand; dann bleibt alles unverändert.
 *
 * Geprüft werden Existenz und Zustand – **nicht** Reichweite und Besitz (ADR 0009, Punkt 8;
 * ADR 0027): Die prüft der Aufrufer über `resolveTarget` und `availableActions`.
 */
export function performAction(state: GameState, action: Action, kontext: InteractionContext = {}): boolean {
  const ziel = action.target;
  if (ziel.kind === 'animal') {
    const animal = tierZu(state, ziel.id);
    if (animal === undefined || !isProductReady(animal, state.tick)) return false;
    const table = kontext.species ?? SPECIES;
    const product = getSpecies(animal.species, table).products.find((p) => tierHandlungsId(p, animal) === action.id);
    if (product === undefined) return false;

    addItem(state.inventory, product.item);
    animal.productReadyTick = state.tick + product.cooldownSeconds * TICK_RATE;
    return true;
  }

  const hof = hofZu(kontext, ziel.parcelId);
  switch (ziel.kind) {
    case 'gate':
      if (hof === undefined) return false;
      hof.gateOpen = !hof.gateOpen;
      return true;
    case 'tree':
      if (hof === undefined || hof.tree.apples <= 0) return false;
      addItem(state.inventory, PARCEL_ACTIONS.tree.item, hof.tree.apples);
      hof.tree.apples = 0;
      return true;
    case 'chest':
      return true;
  }
}

/**
 * Der Quader, auf den man zielt, je festem Ding einer Parzelle: die Zelle des Blocks, so hoch wie
 * das Ding reicht. Der Server misst damit die Nähe, der Client zielt damit.
 */
export function parcelTargetAabb(parcel: Parcel, kind: ParcelTargetKind): Aabb {
  const block = zielBlock(parcel, kind);
  return {
    minX: block.x,
    maxX: block.x + 1,
    minY: block.y,
    maxY: block.y + ZIEL_HOEHE[kind],
    minZ: block.z,
    maxZ: block.z + 1,
  };
}

/**
 * Alle Quader, auf die man bei einem festen Ding zielt. Tor und Kiste haben einen. Der Apfelbaum hat
 * zwei (task-014): den Stamm aus `parcelTargetAabb` und die Krone, so breit wie das Bauteil, von der
 * ersten Laubebene bis zum Wipfel. Ein einziger Quader um den ganzen Baum schlösse eine Kuh unter der
 * Krone ein, und sie wäre nicht mehr zu melken; der Stamm allein ließe den Blick auf einen Apfel am
 * Kronenrand ins Leere gehen. Zielen und die Nähe-Prüfung des Servers nehmen den nächsten Quader.
 */
export function parcelTargetAabbs(parcel: Parcel, kind: ParcelTargetKind): Aabb[] {
  const stamm = parcelTargetAabb(parcel, kind);
  if (kind !== 'tree') return [stamm];
  const { uMin, uMax, vMin, vMax } = HOF_PLAN.apfelbaum;
  const rechteck = parcelLocalRect(parcel, uMin, uMax, vMin, vMax);
  const krone: Aabb = {
    minX: rechteck.minX,
    maxX: rechteck.maxX + 1,
    minY: parcel.tree.y + KRONE_AB,
    maxY: parcel.tree.y + PREFABS.apfelbaum.size[1],
    minZ: rechteck.minZ,
    maxZ: rechteck.maxZ + 1,
  };
  return [stamm, krone];
}

/** Der Punkt, von dem aus geschaut und gemessen wird: Fußpunkt plus Augenhöhe. */
export function augenpunkt(player: PlayerState): Vec3 {
  return { x: player.pos.x, y: player.pos.y + PLAYER.eyeHeight, z: player.pos.z };
}

/** Kleinster Abstand eines Punktes zu einem achsenparallelen Quader; 0 innerhalb. */
export function aabbDistance(punkt: Vec3, quader: Aabb): number {
  const dx = Math.max(quader.minX - punkt.x, 0, punkt.x - quader.maxX);
  const dy = Math.max(quader.minY - punkt.y, 0, punkt.y - quader.maxY);
  const dz = Math.max(quader.minZ - punkt.z, 0, punkt.z - quader.maxZ);
  return Math.hypot(dx, dy, dz);
}

/** Der Block, auf den man zielt, je festem Ding einer Parzelle. */
function zielBlock(parcel: Parcel, kind: ParcelTargetKind): Punkt {
  switch (kind) {
    case 'gate':
      return parcel.gate;
    case 'tree':
      return parcel.tree;
    case 'chest':
      return parcel.chest;
  }
}

/** Der Hof zu einer Parzelle, sofern sein Zustand bekannt ist. */
function hofZu(kontext: InteractionContext, parcelId: number): FarmState | undefined {
  return (kontext.farms ?? []).find((farm) => farm.parcelId === parcelId);
}

/**
 * Ob auf dieser Parzelle gehandelt werden darf. Ohne `parcels` kennt die Welt keinen Besitz (die
 * Technik-Demo); sobald sie da sind, gehört das Ziel dem, dem die Parzelle gehört – und sonst
 * niemandem.
 */
function darfHandeln(parcelId: number | null, kontext: InteractionContext): boolean {
  if (kontext.parcels === undefined) return true;
  if (parcelId === null) return false;
  return kontext.ownParcelId === parcelId;
}

/** Die Parzelle, auf deren Koppel das Tier steht, oder null. */
function parzelleZuTier(animal: AnimalState, kontext: InteractionContext): number | null {
  return (kontext.parcels ?? []).find((parcel) => parcel.penId === animal.penId)?.id ?? null;
}

function tierZu(state: GameState, id: string): AnimalState | undefined {
  return state.animals.find((animal) => animal.id === id);
}

function tierHandlungsId(product: ProductDef, animal: AnimalState): string {
  return `${product.item}-${animal.id}`;
}

function tierHandlung(product: ProductDef, animal: AnimalState): Action {
  return {
    id: tierHandlungsId(product, animal),
    label: product.action,
    key: product.key,
    target: { kind: 'animal', id: animal.id },
  };
}

/** Die Handlung an einem festen Ding der Parzelle, oder null, wenn es gerade nichts zu tun gibt. */
function parzellenHandlung(kind: ParcelTargetKind, parcelId: number, hof: FarmState | undefined): Action | null {
  const id = `${kind}-${parcelId}`;
  const target: InteractTarget = { kind, parcelId };
  switch (kind) {
    case 'gate': {
      // Ohne den Zustand des Hofs wüsste niemand, ob „Öffnen“ oder „Schließen“ dransteht.
      if (hof === undefined) return null;
      const label = hof.gateOpen ? PARCEL_ACTIONS.gate.offen : PARCEL_ACTIONS.gate.zu;
      return { id, label, key: PARCEL_ACTIONS.gate.key, target };
    }
    case 'tree':
      if (hof === undefined || hof.tree.apples <= 0) return null;
      return { id, label: PARCEL_ACTIONS.tree.action, key: PARCEL_ACTIONS.tree.key, target };
    case 'chest':
      return { id, label: PARCEL_ACTIONS.chest.action, key: PARCEL_ACTIONS.chest.key, target };
  }
}

/**
 * Einheitsvektor der Blickrichtung nach der Konvention des Vertrags: `yaw = 0` blickt nach -z,
 * positive Werte drehen nach links; positives `pitch` blickt nach oben.
 */
function blickrichtung(yaw: number, pitch: number): Vec3 {
  const waagerecht = Math.cos(pitch);
  return { x: -Math.sin(yaw) * waagerecht, y: Math.sin(pitch), z: -Math.cos(yaw) * waagerecht };
}

/** Der Quader eines Tiers in seinem eigenen System: Breite in x, Höhe in y, Länge in z. */
function tierQuader(species: Species): Aabb {
  return {
    minX: -species.width / 2,
    maxX: species.width / 2,
    minY: 0,
    maxY: species.height,
    minZ: -species.length / 2,
    maxZ: species.length / 2,
  };
}

/**
 * Strecke vom Auge bis zum Quader des Tiers entlang des Strahls, oder `null`, wenn der Strahl
 * vorbeigeht. Der Test läuft im Tier-System: Fußpunkt als Ursprung, um `-yaw` gedreht, damit das
 * Tier lokal nach -z blickt und sein Quader achsenparallel ist (Breite in x, Länge in z).
 */
function strahlGegenTier(auge: Vec3, richtung: Vec3, animal: AnimalState, species: Species): number | null {
  const start = insTierSystem({ x: auge.x - animal.pos.x, y: auge.y - animal.pos.y, z: auge.z - animal.pos.z }, animal.yaw);
  const d = insTierSystem(richtung, animal.yaw);
  return strahlGegenQuader(start, d, tierQuader(species));
}

/** Abstand eines Punktes zum gedrehten Quader des Tiers – dasselbe Maß, mit dem gezielt wird. */
function abstandZuTier(punkt: Vec3, animal: AnimalState, species: Species): number {
  const lokal = insTierSystem({ x: punkt.x - animal.pos.x, y: punkt.y - animal.pos.y, z: punkt.z - animal.pos.z }, animal.yaw);
  return aabbDistance(lokal, tierQuader(species));
}

/** Dreht einen Vektor aus Weltkoordinaten in das System eines Tiers mit Blickrichtung `yaw`. */
function insTierSystem(v: Vec3, yaw: number): Vec3 {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  return { x: v.x * c - v.z * s, y: v.y, z: v.x * s + v.z * c };
}

/**
 * Slab-Test: Der Strahl `start + t · d` (t ≥ 0) wird je Achse auf das Intervall zwischen den beiden
 * Ebenen des Quaders eingeschränkt; bleibt nach allen drei Achsen ein Intervall übrig, trifft er,
 * und der Anfang des Intervalls ist die Strecke. Beginnt der Strahl im Quader, ist sie 0.
 * Berühren der Oberfläche zählt als Treffer.
 */
function strahlGegenQuader(start: Vec3, d: Vec3, quader: Aabb): number | null {
  const t = { nah: 0, fern: Number.POSITIVE_INFINITY };
  if (!slab(start.x, d.x, quader.minX, quader.maxX, t)) return null;
  if (!slab(start.y, d.y, quader.minY, quader.maxY, t)) return null;
  if (!slab(start.z, d.z, quader.minZ, quader.maxZ, t)) return null;
  return t.nah;
}

/** Eine Achse des Slab-Tests; engt `t` ein und meldet, ob noch ein Intervall bleibt. */
function slab(start: number, d: number, min: number, max: number, t: { nah: number; fern: number }): boolean {
  if (d === 0) {
    // Parallel zu den Ebenen: trifft nur, wenn der Strahl schon zwischen ihnen liegt.
    return start >= min && start <= max;
  }
  const t1 = (min - start) / d;
  const t2 = (max - start) / d;
  t.nah = Math.max(t.nah, Math.min(t1, t2));
  t.fern = Math.min(t.fern, Math.max(t1, t2));
  return t.nah <= t.fern;
}
